import { RuleModel } from '../../../../types';
import { triggerEventOptions } from './trigger-event-options';
import { actionOptions } from './action-options';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTheme, alpha } from '@mui/material/styles';
import PersonOutline from '@mui/icons-material/PersonOutline';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';

interface RuleTemplateCardProps {
  rule: Partial<RuleModel>;
  onUseTemplate: (templateId: number) => void;
}

export default function RuleTemplateCard({
  rule,
  onUseTemplate,
}: RuleTemplateCardProps) {
  const theme = useTheme();

  const triggerType =
    triggerEventOptions.find(
      (opt) => opt.value === rule.triggerInfo?.triggerEvent
    )?.type ||
    rule.triggerInfo?.triggerGroup ||
    'admin';

  const gradientStart = theme.palette.primary.main;
  let gradientEnd = theme.palette.violet[700];

  if (triggerType === 'calendar') {
    gradientEnd = theme.palette.cyan[700];
  } else if (triggerType === 'drive') {
    gradientEnd = theme.palette.slateBlue[900];
  }

  const getIcon = () => {
    const sx = {
      fontSize: 100,
      color: 'rgba(255, 255, 255, 0.2)',
      position: 'absolute',
      right: 0,
      bottom: -14,
    };
    switch (triggerType) {
      case 'admin':
      case 'user':
        return <PersonOutline sx={{ ...sx, bottom: -20 }} />;
      case 'calendar':
        return <EventOutlined sx={sx} />;
      case 'drive':
        return <DescriptionOutlined sx={sx} />;
      default:
        return <PersonOutline sx={sx} />;
    }
  };

  const getCategoryName = () => {
    switch (triggerType) {
      case 'calendar':
        return 'Calendar Events';
      case 'drive':
        return 'Drive Events';
      default:
        return 'Admin Events';
    }
  };

  const getEventName = () => {
    return (
      triggerEventOptions.find(
        (opt) => opt.value === rule.triggerInfo?.triggerEvent
      )?.displayValue || 'Unknown Event'
    );
  };

  const getFilterText = () => {
    if (rule.triggerInfo?.triggerOrgSources?.length) {
      return `Org Unit is ${rule.triggerInfo.triggerOrgSources.join(', ')}`;
    }
    return 'Org Unit is /ORGNAME';
  };

  const getActionText = () => {
    if (!rule.actionInfo?.actionTodo?.length) return '';
    return rule.actionInfo.actionTodo
      .map(
        (action) =>
          actionOptions.find((opt) => opt.value === action)?.displayValue ||
          action
      )
      .join(' + ');
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(90deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
          color: 'white',
          p: 1,
          pl: 2,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 60,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 500, position: 'relative', zIndex: 1 }}
        >
          {rule.basicInfo?.ruleName}
        </Typography>
        <Typography variant="caption" sx={{ position: 'relative', zIndex: 1 }}>
          {getCategoryName()} &gt; {getEventName()}
        </Typography>
        {getIcon()}
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Typography
          variant="body2"
          sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 3 }}
        >
          {rule.basicInfo?.description}
        </Typography>

        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.2),
            borderRadius: 1,
            p: 2,
          }}
        >
          <Stack direction="row" spacing={2} mb={1}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', width: 45 }}
            >
              Filter
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {getFilterText()}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', width: 45 }}
            >
              Action
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {getActionText()}
            </Typography>
          </Stack>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
        <Button
          size="small"
          color="primary"
          sx={{ fontWeight: 500 }}
          onClick={() => onUseTemplate(rule.id!)}
        >
          USE TEMPLATE
        </Button>
      </CardActions>
    </Card>
  );
}
